import React from "react";
import { Text, TouchableOpacity, View } from "react-native";
import type { ConversationRecord } from "../sync/persistence";
import { useChatListStyles } from "./ChatList.styles";

interface ChatListRowProps {
	conversation: ConversationRecord;
	onPress: (conversationId: string) => void;
}

function formatTimestamp(timestamp: number): string {
	const date = new Date(timestamp);
	const now = new Date();
	const diff = now.getTime() - date.getTime();
	const hours = diff / (1000 * 60 * 60);

	if (hours < 24) {
		return date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
		});
	}

	if (hours < 24 * 7) {
		return date.toLocaleDateString("en-US", { weekday: "short" });
	}

	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
	});
}

export const ChatListRow = React.memo<ChatListRowProps>(
	({ conversation, onPress }) => {
		const styles = useChatListStyles();
		const title = conversation.name ?? "Unnamed conversation";
		const initial = title.charAt(0).toUpperCase();
		const preview = conversation.lastMessage?.preview ?? "No messages yet";
		const timestamp = conversation.lastMessage?.timestamp
			? formatTimestamp(conversation.lastMessage.timestamp)
			: "";

		const handlePress = React.useCallback(() => {
			onPress(conversation.id);
		}, [conversation.id, onPress]);

		return (
			<TouchableOpacity
				style={styles.row}
				onPress={handlePress}
				accessibilityRole="button"
				accessibilityLabel={`${title}, ${preview}, ${timestamp}`}
			>
				<View style={styles.avatarContainer}>
					<View style={styles.avatar}>
						<Text style={styles.avatarText}>{initial}</Text>
					</View>
				</View>
				<View style={styles.contentContainer}>
					<View style={styles.topRow}>
						<Text style={styles.nameText} numberOfLines={1}>
							{title}
						</Text>
						{timestamp !== "" && (
							<Text style={styles.timestampText}>{timestamp}</Text>
						)}
					</View>
					<View style={styles.bottomRow}>
						<Text style={styles.messageText} numberOfLines={1}>
							{preview}
						</Text>
						{conversation.unreadCount !== undefined &&
							conversation.unreadCount > 0 && (
								<View style={styles.unreadBadge}>
									<Text style={styles.unreadBadgeText}>
										{conversation.unreadCount > 99
											? "99+"
											: conversation.unreadCount}
									</Text>
								</View>
							)}
					</View>
				</View>
			</TouchableOpacity>
		);
	},
);

ChatListRow.displayName = "ChatListRow";
